import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { setSiteTime } from '../lib/when';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

// Which company site the logged-in person is currently working in, and which
// others they can switch to.
//
// The site isn't tracked in the browser - it lives in the database, in
// user_active_org. That matters: the database uses the same value to decide
// which rows to hand back, so the site shown in the header and the data on the
// screen can never disagree. The browser only asks "which one am I in".
//
// my_orgs()   - the sites this login may work in (all of them for a Global Admin)
// switch_org()- moves them, and refuses sites they have no access to
//
// The site handed back carries what the shell needs to draw itself: which
// modules this site has, its time zone, and whether it writes a 12- or 24-hour
// clock. They ride along with the site rather than being asked for separately,
// because every one of them is needed before the first screen is painted.

const OrgContext = createContext(null);

export function OrgProvider({ children }) {
  const [sites, setSites]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [switching, setSwitching] = useState(false);
  const [error, setError]       = useState('');

  const { user } = useAuth();

  const load = useCallback(async () => {
    if (!user) { setSites([]); setLoading(false); return; }
    const { data, error: e } = await supabase.rpc('my_orgs');
    if (e) setError(e.message);
    else setSites(data || []);
    setLoading(false);
  }, [user]);

  // Only ask once somebody is signed in - on the login screen there is nobody
  // to have sites, and asking anyway just produces a failed request.
  useEffect(() => { load(); }, [load]);

  const current = useMemo(() => sites.find(s => s.is_current) || null, [sites]);

  /*
    **The site's time zone and clock format reach the rest of the app from
    here**, added 5 September 2026. Both settings saved correctly and were read
    by nothing: 54 places printed a date or a time using whatever machine the
    person was sitting at, so two people could read two different times off the
    same submitted count.

    It is set here rather than passed down because most of those 54 places are
    not React components. A CSV builder cannot call a hook.
  */
  useEffect(() => { setSiteTime(current); }, [current]);

  const switchTo = useCallback(async (orgId) => {
    if (!orgId || switching) return;
    setSwitching(true);
    setError('');
    const { error: e } = await supabase.rpc('switch_org', { target_org: orgId });
    if (e) {
      setSwitching(false);
      setError(e.message || 'Could not switch site.');
      return;
    }
    // Every screen's data belongs to the old site, and branding is per-site, so
    // reload rather than trying to refresh each page's state in place.
    // Switching sites is deliberate and rare - a clean reload is worth more
    // here than a slick transition that risks showing one site's rows under
    // another site's name.
    // The company logo cache no longer needs clearing here: BrandingContext
    // keys it by org id, so the reload below simply reads the new site's key.
    window.location.reload();
  }, [switching]);

  const value = useMemo(() => ({
    sites,
    current,
    loading,
    switching,
    error,
    switchTo,
    reload: load,
    hasMultiple: sites.length > 1,
  }), [sites, current, loading, switching, error, switchTo, load]);

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrg() {
  return useContext(OrgContext) || EMPTY;
}

const EMPTY = {
  sites: [], current: null, loading: false, switching: false, error: '',
  switchTo: () => {}, reload: () => {}, hasMultiple: false,
};
